import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getPulseErrorMessage,
  getPulsePayload,
  isPulseApiSuccess,
  listPulseActors,
} from '../../services/pulseApi';
import {
  actorOrderQueryFromActor,
  actorPulseLogKey,
  formatNumber,
  formatPulseDate,
  parseActorOrderSearch,
  parseActorsPayload,
} from './pulseUtils';

const PAGE_SIZE = 40;

function orderPathFromQuery(query) {
  const params = new URLSearchParams();
  if (query.key) params.set('key', query.key);
  if (query.user_id != null && query.user_id !== '') params.set('user_id', String(query.user_id));
  if (query.installation_id) params.set('installation_id', query.installation_id);
  if (query.guest_hash) params.set('guest_hash', query.guest_hash);
  const qs = params.toString();
  return qs ? `/admin/pulse/actors/order?${qs}` : '/admin/pulse/actors/order';
}

function PulseActors() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [source, setSource] = useState(null);
  const [cloudflareHint, setCloudflareHint] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const applied = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
    }),
    [searchParams]
  );

  const [search, setSearch] = useState(applied.search);

  useEffect(() => {
    setSearch(applied.search);
  }, [applied.search]);

  const writeParams = useCallback(
    (next) => {
      const params = {};
      if (next.search.trim()) params.search = next.search.trim();
      if (next.offset) params.offset = String(next.offset);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const loadActors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseActors({
        limit: PAGE_SIZE,
        offset: applied.offset,
        search: applied.search.trim() || undefined,
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load Pulse actors.'));
        setItems([]);
        setTotal(0);
        return;
      }
      const parsed = parseActorsPayload(getPulsePayload(result));
      setItems(parsed.items);
      setTotal(parsed.total);
      setSource(parsed.source);
      setCloudflareHint(parsed.cloudflare_hint);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Pulse actors.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    loadActors();
  }, [loadActors]);

  const applyFilters = () => {
    writeParams({ search, offset: 0 });
  };

  const openOrder = (actor) => {
    const query = actorOrderQueryFromActor(actor);
    navigate(orderPathFromQuery(query));
  };

  const openFromSearch = () => {
    const q = search.trim();
    if (!q) {
      applyFilters();
      return;
    }
    const query = parseActorOrderSearch(q);
    if (query.key || query.user_id || query.installation_id || query.guest_hash) {
      navigate(orderPathFromQuery(query));
      return;
    }
    applyFilters();
  };

  const copyKey = async (key, event) => {
    event?.stopPropagation?.();
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setSnackbar({ open: true, message: `Copied ${key}` });
    } catch {
      setSnackbar({ open: true, message: 'Could not copy to clipboard' });
    }
  };

  const hasMore = applied.offset + items.length < total;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Pulse users / support</Typography>
        <IconButton onClick={loadActors} disabled={loading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Complaint debug: list everyone hitting Pulse, then open one user/installation to see{' '}
        <strong>exactly</strong> which cards BE would serve them right now (after seen / done /
        match continue). Cloudflare / CloudWatch: every app Pulse hit logs{' '}
        <code>pulse:&lt;userId&gt;</code> or <code>pulse:install:&lt;installationId&gt;</code>
        {cloudflareHint ? (
          <>
            {' '}
            — {cloudflareHint}
          </>
        ) : null}
        . If BE <code>current_order</code> matches support but the app differs → FE bug; if order is
        wrong → BE.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && openFromSearch()}
          placeholder="user id · installation id · pulse:…"
          sx={{ minWidth: 320 }}
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={openFromSearch}>
          Search / open
        </Button>
        <Button variant="outlined" onClick={applyFilters} disabled={loading}>
          Filter list
        </Button>
        {source && (
          <Chip
            size="small"
            variant="outlined"
            label={`source: ${source}`}
            title="request_log preferred; card_state is fallback"
          />
        )}
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cloudflare key</TableCell>
              <TableCell>Identity</TableCell>
              <TableCell>Last seen</TableCell>
              <TableCell>Hits</TableCell>
              <TableCell>Last action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No Pulse actors found. Try a user id, installation id, or{' '}
                  <code>pulse:…</code> key.
                </TableCell>
              </TableRow>
            ) : (
              items.map((actor, index) => {
                const logKey = actorPulseLogKey(actor);
                const identity =
                  actor.user_id != null
                    ? `user ${actor.user_id}`
                    : actor.installation_id
                      ? `install ${actor.installation_id}`
                      : actor.guest_hash
                        ? `guest ${actor.guest_hash}`
                        : actor.subject_type && actor.subject_id
                          ? `${actor.subject_type} ${actor.subject_id}`
                          : '—';
                const rowKey =
                  logKey ||
                  `${actor.subject_type || 'actor'}-${actor.subject_id || actor.user_id || index}`;
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openOrder(actor)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          component="code"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {logKey || '—'}
                        </Typography>
                        {logKey ? (
                          <IconButton
                            size="small"
                            title="Copy Cloudflare search key"
                            onClick={(e) => copyKey(logKey, e)}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{identity}</Typography>
                      {actor.guest_hash && actor.user_id != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          guest {actor.guest_hash}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatPulseDate(actor.last_seen_at || actor.lastSeenAt)}
                    </TableCell>
                    <TableCell>{formatNumber(actor.hit_count ?? actor.hitCount ?? 0)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {actor.last_action || actor.lastAction || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={applied.offset <= 0 || loading}
          onClick={() =>
            writeParams({ ...applied, offset: Math.max(0, applied.offset - PAGE_SIZE) })
          }
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total === 0
            ? '0 actors'
            : `${applied.offset + 1}–${applied.offset + items.length} of ${total}`}
        </Typography>
        <Button
          disabled={!hasMore || loading}
          onClick={() => writeParams({ ...applied, offset: applied.offset + PAGE_SIZE })}
        >
          Next
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Needs migration <code>220_pulse_actor_request_log.sql</code>. Row click opens live BE order.
        Or go to{' '}
        <Link to="/admin/pulse/actors/order">order lookup</Link> with a pasted key.
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseActors;
